import { useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Cell,
} from "recharts";
import { API } from "@/lib/api";

const CHART_GRID = "#44403c";
const CHART_TEXT = "#a8a29e";
const CHART_ACCENT = "#d97757";
const ENGINE_COLORS = {
  nt: "#2563eb",
  nvidia_ultra: "#059669",
  nvidia_nemotron: "#0d9488",
  inkling: "#16a34a",
  coding_agent: "#7c3aed",
  ox_alpha: "#0000ff",
  avian_deepseek: "#f59e0b",
  deepseek: "#3b82f6",
  nscale_qwen: "#06b6d4",
  sarvam: "#a855f7",
  gemini: "#eab308",
  claude: "#ef4444",
  openai: "#22c55e",
  openrouter_free: "#64748b",
  glm_free: "#84cc16",
  unknown: "#78716c",
};

function fmtNum(n) {
  if (n == null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function Tile({ label, value, sublabel, testid }) {
  return (
    <div className="border border-[var(--border)] px-5 py-5" data-testid={testid}>
      <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-mono-ui text-2xl text-[var(--text)] mt-2">{value}</div>
      {sublabel && (
        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
          {sublabel}
        </div>
      )}
    </div>
  );
}

/**
 * UserTokenUsageSection — shows the current user's token spend across
 * DeepSeek / Sarvam / Gemini / Claude. No $ cost shown for non-admins
 * (server strips it). Renders inline in /account/usage.
 */
export default function UserTokenUsageSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/account/usage/tokens`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const engineChartData = useMemo(() => {
    if (!stats?.engine_mix) return [];
    return stats.engine_mix.map((e) => ({ ...e, color: ENGINE_COLORS[e.engine] || ENGINE_COLORS.unknown }));
  }, [stats]);

  if (loading) {
    return (
      <div className="border border-[var(--border)] p-5 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
        Loading token usage…
      </div>
    );
  }

  if (err || !stats) {
    return (
      <div className="border border-[var(--border)] p-5 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-red-400">
        {err || "No usage data yet."}
      </div>
    );
  }

  const t = stats.totals || {};
  const noUsage = (t.tokens_all || 0) === 0;

  return (
    <div className="space-y-6" data-testid="user-token-usage">
      {noUsage ? (
        <div className="border border-[var(--border)] p-6 font-ui text-sm text-[var(--text-muted)]">
          No chats yet — start a conversation and your token usage will appear here.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Today" value={fmtNum(t.tokens_24h)} sublabel="tokens (24h)" testid="tok-today" />
            <Tile label="7 days" value={fmtNum(t.tokens_7d)} sublabel="tokens" testid="tok-7d" />
            <Tile label="30 days" value={fmtNum(t.tokens_30d)} sublabel={`${t.calls_30d || 0} chats`} testid="tok-30d" />
            <Tile label="All time" value={fmtNum(t.tokens_all)} sublabel={`${t.calls_all || 0} chats total`} testid="tok-all" />
          </div>

          {stats.daily && stats.daily.length > 0 && (
            <div className="border border-[var(--border)] p-4">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-3">
                Tokens per day (last 30d)
              </div>
              <div className="h-56" data-testid="tok-chart-daily">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: CHART_TEXT, fontSize: 11 }} />
                    <YAxis tick={{ fill: CHART_TEXT, fontSize: 11 }} tickFormatter={fmtNum} />
                    <Tooltip
                      contentStyle={{ background: "var(--accent-deep)", border: "1px solid #444", color: "#fff" }}
                      labelStyle={{ color: "#fff" }}
                    />
                    <Area dataKey="tokens" stroke={CHART_ACCENT} fill={CHART_ACCENT} fillOpacity={0.25} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {engineChartData.length > 0 && (
            <div className="border border-[var(--border)] p-4">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-3">
                By engine
              </div>
              <div className="h-40" data-testid="tok-chart-engine">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engineChartData} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                    <XAxis type="number" tick={{ fill: CHART_TEXT, fontSize: 11 }} tickFormatter={fmtNum} />
                    <YAxis type="category" dataKey="engine" tick={{ fill: CHART_TEXT, fontSize: 11 }} width={70} />
                    <Tooltip
                      contentStyle={{ background: "var(--accent-deep)", border: "1px solid #444", color: "#fff" }}
                      labelStyle={{ color: "#fff" }}
                    />
                    <Bar dataKey="tokens">
                      {engineChartData.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {stats.recent_conversations && stats.recent_conversations.length > 0 && (
            <div className="border border-[var(--border)]" data-testid="tok-recent">
              <div className="px-4 py-3 border-b border-[var(--border)] font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)]">
                Recent conversations
              </div>
              <ul>
                {stats.recent_conversations.map((c) => (
                  <li
                    key={c.conv_id}
                    className="px-4 py-3 border-b border-[var(--border)] last:border-b-0 flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] truncate">
                      {c.conv_id?.slice(0, 24)}
                    </span>
                    <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">
                      {(c.engines || []).join(" · ")}
                    </span>
                    <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text)]">
                      {fmtNum(c.tokens)} tok · {c.calls} msg
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
